import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { useBusiness } from '../contexts/BusinessContext';
import AnnouncementBanner from '../components/ui/AnnouncementBanner';
import {
  Calendar,
  BookOpen,
  Users,
  QrCode,
  User,
  CreditCard,
  Phone,
  MapPin,
  Clock,
  Globe,
  Instagram,
  Facebook,
  Twitter
} from 'lucide-react';

interface MenuCard {
  id: string;
  title: string;
  icon: React.ReactNode;
  href: string;
  gradient: string;
  image?: string;
}

const DashboardPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { currentBusiness } = useBusiness();
  const navigate = useNavigate();

  const menuCards: MenuCard[] = [
    {
      id: 'calendar',
      title: 'RESERVA',
      icon: <Calendar className="w-8 h-8" />,
      href: '/calendar',
      gradient: 'from-purple-600 via-purple-500 to-indigo-600',
      image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&q=80',
    },
    {
      id: 'classes',
      title: 'CLASES',
      icon: <BookOpen className="w-8 h-8" />,
      href: '/classes',
      gradient: 'from-orange-500 via-orange-400 to-amber-500',
      image: 'https://images.unsplash.com/photo-1549576490-b0b4831ef60a?w=400&q=80',
    },
    {
      id: 'instructors',
      title: 'INSTRUCTORES',
      icon: <Users className="w-8 h-8" />,
      href: '/instructors',
      gradient: 'from-emerald-600 via-emerald-500 to-teal-500',
      image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&q=80',
    },
    {
      id: 'my-code',
      title: 'MI CÓDIGO',
      icon: <QrCode className="w-8 h-8" />,
      href: '/my-code',
      gradient: 'from-cyan-600 via-cyan-500 to-blue-500',
      image: 'https://images.unsplash.com/photo-1595079676339-1534801ad6cf?w=400&q=80',
    },
    {
      id: 'profile',
      title: 'MI PERFIL',
      icon: <User className="w-8 h-8" />,
      href: '/profile',
      gradient: 'from-rose-600 via-rose-500 to-pink-500',
      image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&q=80',
    },
    {
      id: 'membership',
      title: 'MEMBRESÍAS Y BONOS',
      icon: <CreditCard className="w-8 h-8" />,
      href: '/memberships',
      gradient: 'from-amber-500 via-yellow-500 to-orange-400',
      image: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=400&q=80',
    },
  ];

  const handleCardClick = (href: string) => {
    navigate(href);
  };

  return (
    <>
      <Helmet>
        <title>Dashboard - {currentBusiness?.name || 'Mi Negocio'}</title>
      </Helmet>

      <div className="space-y-6">
        {/* Cover Image */}
        {currentBusiness?.coverImage && (
          <div className="relative -mx-4 sm:-mx-6 lg:-mx-8 -mt-4 sm:-mt-6 lg:-mt-8 mb-6 h-48 sm:h-64 overflow-hidden rounded-b-3xl shadow-2xl">
            <img
              src={currentBusiness.coverImage}
              alt={currentBusiness.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/50 to-transparent" />

            {/* Logo overlay */}
            <div className="absolute bottom-4 left-4 sm:left-6 lg:left-8 flex items-end gap-4">
              {currentBusiness?.logo && (
                <img
                  src={currentBusiness.logo}
                  alt={currentBusiness.name}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover border-4 border-gray-950 shadow-xl"
                />
              )}
              <div className="mb-1">
                <h1 className="text-2xl sm:text-3xl font-bold text-white drop-shadow-lg">
                  {currentBusiness?.name}
                </h1>
                {currentBusiness?.description && (
                  <p className="text-gray-300 text-sm mt-1 max-w-md line-clamp-2">
                    {currentBusiness.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Header sin cover */}
        {!currentBusiness?.coverImage && (
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              {currentBusiness?.logo && (
                <img
                  src={currentBusiness.logo}
                  alt={currentBusiness.name}
                  className="w-16 h-16 rounded-2xl object-cover"
                />
              )}
              <div>
                <h1 className="text-2xl font-bold text-amber-400">
                  {currentBusiness?.name || 'Mi Negocio'}
                </h1>
                {currentBusiness?.description && (
                  <p className="text-gray-400 text-sm mt-1">
                    {currentBusiness.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Saludo */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-white">
            ¡Hola, {currentUser?.firstName || 'Usuario'}!
          </h2>
          <p className="text-gray-400 mt-1">
            ¿Qué quieres hacer hoy?
          </p>
        </div>

        {/* Anuncios - muestra urgentes como modal y el resto como tarjetas */}
        <AnnouncementBanner showUrgentModal={true} />

        {/* Grid de tarjetas */}
        <div className="grid grid-cols-2 gap-4">
          {menuCards.map((card) => (
            <button
              key={card.id}
              onClick={() => handleCardClick(card.href)}
              className="group relative aspect-square rounded-3xl overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-950"
            >
              {/* Imagen de fondo */}
              {card.image && (
                <div
                  className="absolute inset-0 bg-cover bg-center scale-110 group-hover:scale-100 transition-transform duration-700"
                  style={{ backgroundImage: `url(${card.image})` }}
                />
              )}

              {/* Overlay con gradiente de color - más transparente */}
              <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-60`} />

              {/* Overlay sutil para legibilidad del texto en la parte inferior */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

              {/* Efecto de brillo en hover */}
              <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-300" />

              {/* Contenido */}
              <div className="absolute inset-0 flex flex-col justify-end p-5">
                {/* Icono principal */}
                <div className="mb-3 p-3 bg-black/30 backdrop-blur-sm rounded-2xl w-fit">
                  <div className="text-white">
                    {card.icon}
                  </div>
                </div>

                {/* Título */}
                <h3 className="text-lg font-bold text-white tracking-wide drop-shadow-md">
                  {card.title}
                </h3>
              </div>

              {/* Borde sutil */}
              <div className="absolute inset-0 rounded-3xl border border-white/10" />
            </button>
          ))}
        </div>

        {/* Sección de estado de suscripción */}
        <div className="mt-8">
          <div className="bg-gray-900/80 backdrop-blur-sm rounded-3xl p-6 border border-gray-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-2xl ${currentUser?.activeMembership?.isActive
                  ? 'bg-amber-500/20'
                  : currentUser?.activePass && currentUser.activePass.creditsRemaining > 0
                    ? 'bg-blue-500/20'
                    : 'bg-gray-500/20'
                  }`}>
                  <CreditCard className={`w-6 h-6 ${currentUser?.activeMembership?.isActive
                    ? 'text-amber-400'
                    : currentUser?.activePass && currentUser.activePass.creditsRemaining > 0
                      ? 'text-blue-400'
                      : 'text-gray-400'
                    }`} />
                </div>
                <div>
                  <h3 className="text-white font-semibold">
                    {currentUser?.activeMembership?.isActive
                      ? 'Membresía Activa'
                      : currentUser?.activePass && currentUser.activePass.creditsRemaining > 0
                        ? 'Bono Activo'
                        : 'Estado de Suscripción'
                    }
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {currentUser?.activeMembership?.isActive
                      ? currentUser.activeMembership.membershipName || 'Cuota Mensual'
                      : currentUser?.activePass && currentUser.activePass.creditsRemaining > 0
                        ? `${currentUser.activePass.passTypeName || currentUser.activePass.passName || 'Bono'} - ${currentUser.activePass.creditsRemaining} clases restantes`
                        : 'Sin suscripción activa'
                    }
                  </p>
                </div>
              </div>
              <div className={`px-4 py-2 rounded-full text-sm font-medium ${currentUser?.activeMembership?.isActive
                ? 'bg-amber-500/20 text-amber-400'
                : currentUser?.activePass && currentUser.activePass.creditsRemaining > 0
                  ? 'bg-blue-500/20 text-blue-400'
                  : 'bg-gray-500/20 text-gray-400'
                }`}>
                {currentUser?.activeMembership?.isActive
                  ? 'Membresía'
                  : currentUser?.activePass && currentUser.activePass.creditsRemaining > 0
                    ? 'Bono'
                    : 'Inactiva'
                }
              </div>
            </div>
          </div>
        </div>

        {/* Información del negocio */}
        {(currentBusiness?.phone || currentBusiness?.address || currentBusiness?.openingHours || currentBusiness?.website) && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-white mb-4">Información de Contacto</h3>
            <div className="bg-gray-900/80 backdrop-blur-sm rounded-3xl p-6 border border-gray-800 space-y-4">
              {currentBusiness?.openingHours && (
                <div className="flex items-center gap-3 text-gray-300">
                  <Clock className="w-5 h-5 text-amber-400" />
                  <span>{currentBusiness.openingHours}</span>
                </div>
              )}
              {currentBusiness?.phone && (
                <div className="flex items-center gap-3 text-gray-300">
                  <Phone className="w-5 h-5 text-amber-400" />
                  <a href={`tel:${currentBusiness.phone}`} className="hover:text-white transition-colors">
                    {currentBusiness.phone}
                  </a>
                </div>
              )}
              {currentBusiness?.address && (
                <div className="flex items-center gap-3 text-gray-300">
                  <MapPin className="w-5 h-5 text-amber-400" />
                  <span>{currentBusiness.address}</span>
                </div>
              )}
              {currentBusiness?.website && (
                <div className="flex items-center gap-3 text-gray-300">
                  <Globe className="w-5 h-5 text-amber-400" />
                  <a
                    href={currentBusiness.website.startsWith('http') ? currentBusiness.website : `https://${currentBusiness.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors"
                  >
                    {currentBusiness.website}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Redes Sociales */}
        {(currentBusiness?.socialMedia?.instagram || currentBusiness?.socialMedia?.facebook || currentBusiness?.socialMedia?.twitter) && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-white mb-4">Síguenos</h3>
            <div className="flex gap-4">
              {currentBusiness?.socialMedia?.instagram && (
                <a
                  href={currentBusiness.socialMedia.instagram.startsWith('http')
                    ? currentBusiness.socialMedia.instagram
                    : `https://instagram.com/${currentBusiness.socialMedia.instagram.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 rounded-2xl text-white hover:scale-105 transition-transform shadow-lg"
                >
                  <Instagram className="w-7 h-7" />
                </a>
              )}
              {currentBusiness?.socialMedia?.facebook && (
                <a
                  href={currentBusiness.socialMedia.facebook.startsWith('http')
                    ? currentBusiness.socialMedia.facebook
                    : `https://facebook.com/${currentBusiness.socialMedia.facebook}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-14 h-14 bg-blue-600 rounded-2xl text-white hover:scale-105 transition-transform shadow-lg"
                >
                  <Facebook className="w-7 h-7" />
                </a>
              )}
              {currentBusiness?.socialMedia?.twitter && (
                <a
                  href={currentBusiness.socialMedia.twitter.startsWith('http')
                    ? currentBusiness.socialMedia.twitter
                    : `https://twitter.com/${currentBusiness.socialMedia.twitter.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-14 h-14 bg-gray-800 rounded-2xl text-white hover:scale-105 transition-transform shadow-lg"
                >
                  <Twitter className="w-7 h-7" />
                </a>
              )}
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default DashboardPage;
